/* Welle 5 Phase 3 — Mobile AI Advisor as a TRUE fullscreen modal.
 *
 * The FAB (bottom-right, `<lg`) opens a portal-rendered
 * `position:fixed; inset:0` layer at z-[2100] — above the bottom tab
 * bar (z-40), sticky header (z-30), Leaflet panes and shadcn sheets
 * (z-50).
 *
 * Hard requirements implemented here:
 *   - fixed header / fixed composer — ONLY the message area scrolls
 *     (layout lives in AIAdvisorPanel's `fullscreen` variant)
 *   - iOS safe areas top+bottom (index.html has viewport-fit=cover)
 *   - keyboard avoidance: container height tracks visualViewport so
 *     the composer is never covered by the on-screen keyboard
 *   - body scroll lock without leaks (position:fixed + scroll restore)
 *   - browser/hardware Back closes the modal instead of leaving the page
 *   - conversation + draft input survive close/reopen: the modal stays
 *     mounted and is only display-toggled
 *   - `?ai=open` deep-link (DealCard "KI fragen") auto-opens on <lg
 */
import i18n from "../i18n";
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";
import AIAdvisorPanel from "./AIAdvisorPanel";
import { useVisualViewport } from "../hooks/useVisualViewport";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const HISTORY_FLAG = "dealalertAiFullscreen";

export default function AIAdvisorMobileFab({
  listingUlid,
  vertical,
  openSignal,
  card = null,
  restoreSignal = null,
  onRestoreComplete = null,
}) {
  const [open, setOpen] = useState(false);
  const vv = useVisualViewport(open);

  // `?ai=open` deep-link → auto-open the fullscreen advisor on <lg
  // (the desktop side panel is CSS-hidden there, so without this the
  // deep-link would land nowhere on mobile).
  useEffect(() => {
    if (openSignal && window.matchMedia("(max-width: 1023px)").matches) {
      setOpen(true);
    }
  }, [openSignal]);

  // Klagenfurt P0.4d — a widget-path BACK-navigation on mobile lands
  // here with a `restoreSignal` set. The fullscreen advisor MUST be
  // presented for the inner AIAdvisorPanel to actually reach the
  // scroll container and land the restore.
  useEffect(() => {
    if (
      restoreSignal
      && window.matchMedia("(max-width: 1023px)").matches
    ) {
      setOpen(true);
    }
  }, [restoreSignal]);

  // Safety valve: resizing to ≥lg while open would leave an invisible
  // modal holding the body scroll lock — close it instead.
  useEffect(() => {
    if (!open) return undefined;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => {
      if (e.matches) setOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [open]);

  // iOS-proof body scroll lock — shared hook (no leaks).
  useBodyScrollLock(open);

  // Browser/hardware Back closes the modal, not the page.
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ [HISTORY_FLAG]: true }, "");
    const onPop = () => setOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);

  const close = useCallback(() => {
    if (window.history.state && window.history.state[HISTORY_FLAG]) {
      window.history.back(); // popstate handler flips `open`
    } else {
      setOpen(false);
    }
  }, []);

  return (
    <>
      <button
        type="button"
        data-testid="ai-advisor-mobile-fab"
        onClick={() => setOpen(true)}
        aria-label={i18n.t("common:ui.openAi")}
        className="lg:hidden fixed bottom-20 right-4 z-40
                   h-14 w-14 rounded-full
                   bg-gradient-to-br from-emerald-400 to-teal-500
                   text-zinc-950 font-bold
                   shadow-[0_4px_20px_rgba(16,185,129,0.45)]
                   ring-2 ring-emerald-300/40
                   active:scale-95 transition-transform
                   flex items-center justify-center"
      >
        <Sparkles className="h-6 w-6" strokeWidth={2.4} />
      </button>

      {createPortal(
        <div
          data-testid="ai-fullscreen-modal"
          role="dialog"
          aria-modal="true"
          className="lg:hidden fixed inset-x-0 top-0 z-[2100] flex-col bg-zinc-950 h-[100dvh]"
          style={{
            display: open ? "flex" : "none",
            height: vv.height ? `${vv.height}px` : undefined,
            transform: vv.offsetTop ? `translateY(${vv.offsetTop}px)` : undefined,
          }}
        >
          <AIAdvisorPanel
            listingUlid={listingUlid}
            vertical={vertical}
            card={card}
            fullscreen
            onRequestClose={close}
            restoreSignal={restoreSignal}
            onRestoreComplete={onRestoreComplete}
            snapshotterActive={open}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
